import { push, ref } from "firebase/database";
import { Form, Formik } from "formik";
import moment from "moment-timezone";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { Accordion, Alert, Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import slugify from "react-slugify";
import * as Yup from "yup";
import FFieldCheckbox from "../../../../../components/Form/FFieldCheckbox";
import FFieldInput from "../../../../../components/Form/FFieldInput";
import FFieldSelect from "../../../../../components/Form/FFieldSelect";
import FFieldSwitch from "../../../../../components/Form/FFieldSwitch";
import FFieldTextarea from "../../../../../components/Form/FFieldTextarea";
import { InputTypeE, OptionI } from "../../../../../components/Form/types";
import { database } from "../../../../../firebase";
import { objectToArray, setPageTitle } from "../../../../../functions";
import { useGetDbData } from "../../../../../hooks/getDbData";
import { GlobalContext } from "../../../../../store";
import { FileFormatI, GameGenreI, GameI, PlatformI } from "../../../../../types";

export const AddNewGame: React.FC = () => {
    const { t } = useTranslation();
    const store = useContext(GlobalContext);
    const navigate = useNavigate();

    const [genreOptions, setGenreOptions] = useState<OptionI[]>();
    const [controlOptions, setControlOptions] = useState<OptionI[]>();
    const [fileFormatOptions, setFileFormatOptions] = useState<OptionI[]>();
    const [platformOptions, setPlatformOptions] = useState<OptionI[]>();
    const [gameAdded, setGameAdded] = useState<boolean | undefined>();

    const { sendRequest: fetchDbData } = useGetDbData();

    const initialValues: GameI = {
        title: "",
        genre: [],
        rating: {
            graphic: 0,
            music: 0,
            sound: 0,
            total: 0,
            intro: 0,
        },
        publisher: "",
        intro: "",
        control: [],
        players: 0,
        format: "",
        date: {
            added: moment().format("YYYY-MM-DD HH:mm:ss"),
            testing: "",
            published: "",
            cracked: "",
        },
        description: "",
        picture: {
            intro: "",
            menu: "",
            docu: "",
            poster: "",
            map: "",
            cartridge: "",
            inGameNr: 0,
            inGame: "GAME_NAME-game-[N]",
        },
        externalLink: "",
        platform: "",
        author: store.loggedUser?.email ?? "N/A",
        trainer: false,
        url: "",
        manual: "",
        instructions: "",
        cheats: "",
        metaDesc: "",
        metaKeys: "",
        jpgImages: false,
    };

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(t("This field is mandatory").toString()),
        url: Yup.string().required(t("This field is mandatory").toString()),
        // genre: Yup.array()
        //     .min(1, t("At least one option needs to be selected").toString())
        //     .required(t("This field is mandatory").toString()),
        // rating: Yup.object({
        //     graphic: Yup.number().required(
        //         t("This field is mandatory").toString()
        //     ),
        // }),
        format: Yup.string().required(t("This field is mandatory").toString()),
        date: Yup.object({
            testing: Yup.date().required(t("This field is mandatory").toString()),
        }),
        description: Yup.string().required(t("This field is mandatory").toString()),
        platform: Yup.string().required(t("This field is mandatory").toString()),
    });

    const ratingOptions: OptionI[] = [
        {
            label: "",
            value: undefined,
        },
    ];
    [...Array(10)].forEach((x, i) => {
        ratingOptions.push({
            label: String(i + 1) + "/10",
            value: String(i + 1),
        });
    });

    const handleSubmit = (values: GameI, resetForm: () => void) => {
        push(ref(database, "game"), values)
            .then((result) => {
                setGameAdded(true);
                window.scrollTo(0, 0);

                setTimeout(() => {
                    resetForm();
                }, 1000);
            })
            .catch((error) => {
                setGameAdded(false);
            });
    };

    // Protected page!!!
    useEffect(() => {
        // User is not logged in, redirect him at Login page
        if (!store.loggedUser) {
            navigate("/login");
        }
        // eslint-disable-next-line
    }, [store.loggedUser]);

    useEffect(() => {
        setPageTitle(t("Add new game"));

        fetchDbData<GameGenreI>({ table: "genre", orderBy: "use", where: true }, (dbData) => {
            const result = objectToArray<GameGenreI>(dbData).sort((a, b) => a.name.localeCompare(b.name));
            const options: OptionI[] = [];

            result.forEach((genre: GameGenreI) => {
                options.push({
                    label: t(genre.name),
                    value: genre.name,
                });
            });

            setGenreOptions(options);
        });

        fetchDbData<string>({ table: "controller" }, (dbData) => {
            const result = objectToArray<string>(dbData).sort((a, b) => a.localeCompare(b));
            const options: OptionI[] = [];

            result.forEach((control) => {
                options.push({
                    label: control,
                    value: control,
                });
            });

            setControlOptions(options);
        });

        fetchDbData<FileFormatI>({ table: "format" }, (dbData) => {
            const result = objectToArray<FileFormatI>(dbData).sort((a, b) => a.title.localeCompare(b.title));
            const options: OptionI[] = [
                {
                    label: "",
                    value: "",
                },
            ];

            result.forEach((fileFormat: FileFormatI) => {
                options.push({
                    label: `${fileFormat.title} (*.${fileFormat.extension})`,
                    value: `${fileFormat.title} (*.${fileFormat.extension})`,
                });
            });
            setFileFormatOptions(options);
        });

        fetchDbData<PlatformI>({ table: "platform" }, (dbData) => {
            const result = objectToArray<PlatformI>(dbData).sort((a, b) => a.title.localeCompare(b.title));
            const options: OptionI[] = [
                {
                    label: "",
                    value: "",
                },
            ];

            result.forEach((platform: PlatformI) => {
                options.push({
                    label: platform.title,
                    value: platform.title,
                });
            });
            setPlatformOptions(options);
        });
        // eslint-disable-next-line
    }, []);

    return (
        <Card>
            <Card.Header>{t("Add new game")}</Card.Header>
            <Card.Body>
                {gameAdded ? <Alert variant={"info"}>{t("Game added successfuly")}.</Alert> : null}

                {typeof gameAdded !== "undefined" && !gameAdded ? (
                    <Alert variant={"warning"}>
                        {t("An error occured during adding game")}!
                        <br />
                        {t("Try again, please")}...
                    </Alert>
                ) : null}

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values: GameI, { resetForm }) => {
                        handleSubmit(values, resetForm);
                    }}
                >
                    {(formApi) => (
                        <Form>
                            <FFieldInput
                                formName={"title"}
                                label={t("Title")}
                                required
                                onBlur={(value) => {
                                    formApi.setFieldValue("url", slugify(value), false);
                                }}
                                className={"mb-3"}
                                autoFocus
                            />

                            <FFieldCheckbox
                                options={genreOptions}
                                formName={"genre"}
                                label={t("Genre")}
                                className={"mb-3"}
                            />

                            <Accordion className={"mb-3"} defaultActiveKey={"rating"}>
                                <Accordion.Item eventKey={"rating"}>
                                    <Accordion.Header>{t("Rating")}:</Accordion.Header>
                                    <Accordion.Body>
                                        <FFieldSelect
                                            options={ratingOptions}
                                            formName={"rating.graphic"}
                                            label={t("Graphics")}
                                            className={"mb-3"}
                                        />

                                        <FFieldSelect
                                            options={ratingOptions}
                                            formName={"rating.music"}
                                            label={t("Music")}
                                            className={"mb-3"}
                                        />

                                        <FFieldSelect
                                            options={ratingOptions}
                                            formName={"rating.sound"}
                                            label={t("Sound")}
                                            className={"mb-3"}
                                        />

                                        <FFieldSelect
                                            options={ratingOptions}
                                            formName={"rating.intro"}
                                            label={"Intro"}
                                            className={"mb-3"}
                                        />

                                        <FFieldSelect
                                            options={ratingOptions}
                                            formName={"rating.total"}
                                            label={t("Total")}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <FFieldInput formName={"publisher"} label={t("Publisher")} className={"mb-3"} />

                            <FFieldInput formName={"intro"} label={"Intro"} className={"mb-3"} />

                            <Accordion className={"mb-3"} defaultActiveKey={"dates"}>
                                <Accordion.Item eventKey={"dates"}>
                                    <Accordion.Header>{t("Dates")}:</Accordion.Header>
                                    <Accordion.Body>
                                        <FFieldInput
                                            formName={"date.testing"}
                                            label={t("Testing date")}
                                            required
                                            type={InputTypeE.DATE}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"date.published"}
                                            label={t("Publishing date")}
                                            type={InputTypeE.DATE}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"date.cracked"}
                                            label={t("Cracked date")}
                                            type={InputTypeE.DATE}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <FFieldInput
                                formName={"players"}
                                label={t("Players amount")}
                                type={InputTypeE.NUMBER}
                                className={"mb-3"}
                            />

                            <FFieldCheckbox
                                options={controlOptions}
                                formName={"control"}
                                label={t("Controls")}
                                className={"mb-3"}
                            />

                            <FFieldSwitch formName={"trainer"} label={t("Game have trainer")} className={"mb-3"} />

                            <FFieldSelect
                                options={fileFormatOptions}
                                formName={"format"}
                                label={t("File Format")}
                                required
                                className={"mb-3"}
                            />

                            <FFieldSwitch formName={"jpgImages"} label={`*.jpg ${t("images")}`} className={"mb-3"} />

                            <Accordion className={"mb-3"} defaultActiveKey={"files-pictures"}>
                                <Accordion.Item eventKey="files-pictures">
                                    <Accordion.Header>
                                        {t("Files")} - {t("pictures")}:
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <FFieldInput
                                            formName={"picture.intro"}
                                            label={t("Intro Picture")}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"picture.menu"}
                                            label={t("Menu Picture")}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"picture.docu"}
                                            label={t("Docs Picture")}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"picture.poster"}
                                            label={t("Poster Picture")}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"picture.map"}
                                            label={t("Map Picture")}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"picture.cartridge"}
                                            label={t("Cartridge Picture")}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"picture.inGame"}
                                            label={t("In-game Picture")}
                                            className={"mb-3"}
                                        />

                                        <FFieldInput
                                            formName={"picture.inGameNr"}
                                            label={t("Number of In-game Pictures")}
                                            type={InputTypeE.NUMBER}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <FFieldTextarea
                                formName={"description"}
                                label={t("Description")}
                                required
                                className={"mb-3"}
                            />

                            <FFieldInput
                                formName={"externalLink"}
                                label={t("External Review URL")}
                                className={"mb-3"}
                            />

                            <FFieldSelect
                                options={platformOptions}
                                formName={"platform"}
                                label={t("Platform")}
                                required
                                className={"mb-3"}
                                onBlur={(value) => {
                                    formApi.setFieldValue("url", slugify(value) + "-" + formApi.values.url, false);
                                }}
                            />

                            <FFieldInput formName={"url"} label={t("URL")} required className={"mb-3"} />

                            <FFieldTextarea formName={"manual"} label={t("Manual")} className={"mb-3"} />

                            <FFieldTextarea formName={"instructions"} label={t("Instructions")} className={"mb-3"} />

                            <FFieldTextarea formName={"cheats"} label={t("Cheats")} className={"mb-3"} />

                            <FFieldTextarea formName={"metaDesc"} label={t("Meta Description")} className={"mb-3"} />

                            <FFieldTextarea formName={"metaKeys"} label={t("Meta Keywords")} className={"mb-3"} />

                            <Button
                                type={"submit"}
                                size={"lg"}
                                className={"d-block w-100"}
                                disabled={!formApi.isValid || Object.keys(formApi.errors).length > 0 || !formApi.dirty}
                            >
                                {t("Add Game")}
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};
